import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Swiper from "swiper";
import { Pagination, Navigation } from "swiper/modules";
import Lightbox from "yet-another-react-lightbox";
import Thumbnails from "yet-another-react-lightbox/plugins/thumbnails";
import Zoom from "yet-another-react-lightbox/plugins/zoom";

import type { TransformedProduct } from "../../../types/productApiTypes";
import type { ImageSourceType } from "../../../../../../types/globalTypes";
import { resolveGalleryImages } from "../../../helpers/productApiHelpers/imageResolver";
import Icon from "../../../../../../common/components/Icon/Icon";

import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";

import "yet-another-react-lightbox/styles.css";
import "yet-another-react-lightbox/plugins/thumbnails.css";

interface ProductInfoGalleryProps {
  product: TransformedProduct;
  selectedVariation: TransformedProduct | null;
}

const ProductInfoGallery: React.FC<ProductInfoGalleryProps> = ({
  product,
  selectedVariation,
}) => {
  const { t, ready: areTranslationsReady } = useTranslation();
  const swiperRef = useRef<HTMLDivElement>(null);

  const [lightboxIndex, setLightboxIndex] = useState<number | undefined>(
    undefined,
  );

  // --- Read Global UI Configuration ---
  const gallerySettings = window.commonVariables?.uiSettings?.productGallery;
  const showNavigation = gallerySettings?.showNavigation || false;

  const resolutionSequence =
    window.commonVariables?.uiSettings?.imageResolution?.productGallery
      ?.sequence ||
    ([
      "VARIATION_MAIN",
      "VARIATION_GALLERY",
      "PARENT_MAIN",
      "PARENT_GALLERY",
    ] as ImageSourceType[]);

  const imagesToShow = useMemo(() => {
    return resolveGalleryImages(product, selectedVariation, resolutionSequence);
  }, [product, selectedVariation, resolutionSequence]);

  const slides = imagesToShow.map((image) => ({
    src: image.src,
    alt: image.alt || product.name,
  }));

  useEffect(() => {
    let mainSlider: Swiper | null = null;

    if (swiperRef.current) {
      const swiperModules = [Pagination];
      if (showNavigation) swiperModules.push(Navigation);

      mainSlider = new Swiper(swiperRef.current, {
        modules: swiperModules,
        direction: "horizontal",
        loop: true,
        spaceBetween: 0,
        pagination: {
          el: ".swiper-pagination",
          clickable: true,
        },
        ...(showNavigation && {
          navigation: {
            nextEl: ".swiper-button-next",
            prevEl: ".swiper-button-prev",
          },
        }),
      });
    }

    return () => {
      // --- SWIPER BUG FIX: Purge clones and inline CSS on unmount ---
      mainSlider?.destroy(true, true);
    };
  }, [imagesToShow, showNavigation]);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
  };

  if (!areTranslationsReady) return null;

  return (
    <>
      <div className="product-info-gallery">
        {/* --- SWIPER BUG FIX: Force React to rebuild Swiper DOM to clear cached loop clones --- */}
        <div
          className="swiper main-swiper"
          ref={swiperRef}
          key={selectedVariation ? selectedVariation.id : product.id}
        >
          <div className="swiper-wrapper">
            {imagesToShow.map((image, index) => (
              <div
                key={image.id}
                className="swiper-slide"
                onClick={() => openLightbox(index)}
                style={{ cursor: "pointer" }}
              >
                <img
                  src={image.thumbnail}
                  data-srcset={image.srcset}
                  data-sizes="auto"
                  className="lazyload"
                  alt={image.alt || product.name}
                />
                <button
                  className="product-info-gallery__zoom-icon"
                  aria-label={t(
                    "productInfoGallery.enlargeImage",
                    "Enlarge image",
                  )}
                  onClick={(e) => {
                    e.stopPropagation();
                    openLightbox(index);
                  }}
                >
                  <Icon
                    iconName="fullscreen-outlined"
                    className="icon--stroke-thick"
                  />
                </button>
              </div>
            ))}
          </div>

          <div className="swiper-pagination"></div>

          {showNavigation && (
            <>
              <div className="swiper-button-prev"></div>
              <div className="swiper-button-next"></div>
            </>
          )}
        </div>
      </div>

      <Lightbox
        open={lightboxIndex !== undefined}
        close={() => setLightboxIndex(undefined)}
        slides={slides}
        index={lightboxIndex}
        plugins={[Thumbnails, Zoom]}
        zoom={{ maxZoomPixelRatio: 3 }}
        thumbnails={{ border: 0, padding: 2, gap: 8 }}
      />
    </>
  );
};

export default ProductInfoGallery;
